import React, { createContext, useReducer, useContext, useCallback, useRef } from 'react';
import { customerReducer, initialState } from './customerReducer';
import { CustomerService } from '../services/CustomerService';

const CustomersContext = createContext();

/**
 * CustomersProvider — holds the WHOLE customer base, so it loads ON DEMAND.
 *
 * This provider used to fetch every customer on mount. It wraps the entire
 * customers tab, so simply OPENING ONE CUSTOMER'S PROFILE downloaded the full
 * customerBase tree — ~180MB over 111k customers — none of which the profile
 * or the (server-paged) customer list ever reads. That single mount was the
 * largest Realtime Database egress charge in the app.
 *
 * Nothing is fetched now until a screen that genuinely needs the whole base
 * asks for it via loadAllCustomers() — today only Customer Analytics.
 */
export const CustomersProvider = ({ children }) => {
    const [state, dispatch] = useReducer(customerReducer, initialState);
    const inFlight = useRef(null);

    const loadAllCustomers = useCallback(async () => {
        if (inFlight.current) return inFlight.current;      // never two full reads at once
        inFlight.current = (async () => {
            dispatch({ type: 'SET_LOADING', payload: true });
            try {
                // Analytics no longer comes through here — it aggregates server-side.
                // Anything still calling this gets the refusal from CustomerService
                // rather than a silent 177 MB download.
                const data = await CustomerService.fetchAllCustomers();
                dispatch({ type: 'SET_CUSTOMERS', payload: data });
                return data;
            } catch (error) {
                dispatch({ type: 'SET_ERROR', payload: error.message });
                return [];
            } finally {
                inFlight.current = null;
            }
        })();
        return inFlight.current;
    }, []);

    return (
        <CustomersContext.Provider value={{ state, dispatch, loadAllCustomers }}>
            {children}
        </CustomersContext.Provider>
    );
};

export const useCustomers = () => {
    const context = useContext(CustomersContext);
    if (!context) {
        throw new Error("useCustomers must be used within a CustomersProvider");
    }
    return context;
};
